import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

interface TaskIssue {
  id: string;
  taskId: string;
  name: string;
  name2?: string;
  description?: string;
  severity: string;
  status: string;
  reportedBy: string;
  reportedName: string;
  reportedName2: string;
  reportedDate: string;
  ameStatus?: number;
  isAme?: boolean;
  createdAt?: string;
  updatedAt?: string;
  reportedByName?: string;
}

// ==================== Async Thunks ====================

// 🔵 جلب القضايا حسب رقم المهمة
export const getTaskIssuesByTaskId = createAsyncThunk(
  'TaskIssue/getTaskIssuesByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskIssueByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🟢 إضافة قضية
export const addTaskIssue = createAsyncThunk(
  'TaskIssue/addTaskIssue',
  // async (item: Omit<TaskIssue, 'id' | 'reportedByName' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskIssue', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة القضية');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ✏️ تعديل قضية
export const updateTaskIssue = createAsyncThunk(
  'TaskIssue/updateTaskIssue',
  // async (item: TaskIssue, { rejectWithValue }) => {
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskIssue', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تحديث القضية');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🗑️ حذف قضية
export const deleteTaskIssue = createAsyncThunk(
  'TaskIssue/deleteTaskIssue',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskIssue?id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في حذف القضية');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Slice ====================

interface TaskIssueState {
  taskIssueList: TaskIssue[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskIssueState = {
  taskIssueList: [],
  loading: false,
  error: null
};

const TaskIssueSlice = createSlice({
  name: 'TaskIssue',
  initialState,
  reducers: {
    clearTaskIssues: state => {
      state.taskIssueList = [];
      state.error = null;
      state.loading = false;
    },
    addLocalTaskIssue: (state, action: PayloadAction<TaskIssue>) => {
      state.taskIssueList.unshift(action.payload);
    },
    updateLocalTaskIssue: (state, action: PayloadAction<TaskIssue>) => {
      const index = state.taskIssueList.findIndex(i => i.id === action.payload.id);
      if (index !== -1) state.taskIssueList[index] = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      // 🔄 Get
      .addCase(getTaskIssuesByTaskId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTaskIssuesByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskIssueList = action.payload;
      })
      .addCase(getTaskIssuesByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ➕ Add
      .addCase(addTaskIssue.fulfilled, (state, action) => {
        state.taskIssueList.unshift(action.payload);
      })
      .addCase(addTaskIssue.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // ✏️ Update
      .addCase(updateTaskIssue.fulfilled, (state, action) => {
        const index = state.taskIssueList.findIndex(i => i.id === action.payload.id);
        if (index !== -1) state.taskIssueList[index] = action.payload;
      })
      .addCase(updateTaskIssue.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // 🗑️ Delete
      .addCase(deleteTaskIssue.fulfilled, (state, action) => {
        state.taskIssueList = state.taskIssueList.filter(i => i.id !== action.payload);
      })
      .addCase(deleteTaskIssue.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearTaskIssues, addLocalTaskIssue, updateLocalTaskIssue } = TaskIssueSlice.actions;
export default TaskIssueSlice.reducer;
